import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../../api/ApiClientContext';
import { UserHabitResponse } from '../../api/Api';
import { Icon } from '../../assets/icons/Icon';
import Spinner from '../spinner/Spinner';
import styles from './HabitTrackingHistory.module.scss';

type HistoryStatus = 'active' | 'complete' | 'incomplete';

const ICON_NAMES = ['OneIcon', 'TwoIcon', 'ThreeIcon', 'FourIcon'] as const;

const SECTIONS: { key: HistoryStatus; label: string }[] = [
  { key: 'active', label: 'Active habits' },
  { key: 'complete', label: 'Complete' },
  { key: 'incomplete', label: 'Incomplete' },
];

function getIconForIndex(index: number) {
  return ICON_NAMES[index % ICON_NAMES.length];
}

function getStatusClass(status: HistoryStatus) {
  switch (status) {
    case 'active':
      return styles.iconWrapperActive;
    case 'complete':
      return styles.iconWrapperComplete;
    case 'incomplete':
      return styles.iconWrapperIncomplete;
  }
}

function getBarClass(status: HistoryStatus) {
  switch (status) {
    case 'active':
      return styles.progressFillActive;
    case 'complete':
      return styles.progressFillComplete;
    case 'incomplete':
      return styles.progressFillIncomplete;
  }
}

function groupByStatus(habits: UserHabitResponse[]): Record<HistoryStatus, UserHabitResponse[]> {
  const groups: Record<HistoryStatus, UserHabitResponse[]> = {
    active: [],
    complete: [],
    incomplete: [],
  };
  habits.forEach((h) => {
    const status = (h.history_status as HistoryStatus) || 'incomplete';
    if (groups[status]) {
      groups[status].push(h);
    } else {
      groups.incomplete.push(h);
    }
  });
  return groups;
}

const HabitCard = ({
  habit,
  status,
  index,
}: {
  habit: UserHabitResponse;
  status: HistoryStatus;
  index: number;
}) => {
  const daysCompleted = habit.days_completed ?? 0;
  const minDays = habit.habit.min_completion_days ?? 30;
  const pct = Math.min(100, minDays > 0 ? (daysCompleted / minDays) * 100 : 0);
  const programmeName = habit.habit.programme_name;

  return (
    <div className={styles.card}>
      <div className={styles.cardInner}>
        <div className={`${styles.iconWrapper} ${getStatusClass(status)}`}>
          <Icon iconName={getIconForIndex(index)} width="3.5rem" height="3.5rem" />
        </div>
        <div className={styles.cardBody}>
          <div className={styles.cardHeader}>
            {programmeName && <span className={styles.badge}>{programmeName}</span>}
          </div>
          <p className={styles.habitName}>{habit.habit.name}</p>
          <div className={styles.progressSection}>
            <div className={styles.progressHeader}>
              <span className={styles.progressLabel}>Times tracked</span>
              <span className={styles.progressValue}>
                {daysCompleted}/{minDays}
              </span>
            </div>
            <div className={styles.progressTrack}>
              <div
                className={`${styles.progressFill} ${getBarClass(status)}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const HabitTrackingHistory = () => {
  const apiClient = useApiClient();

  const { data: habits, isLoading } = useQuery({
    queryKey: ['habitHistory'],
    queryFn: async () => {
      const res = await apiClient.api.getMyActiveHabitsApiV1HabitsMyActiveGet({
        include_history: true,
      });
      return res?.data ?? [];
    },
  });

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner />
      </div>
    );
  }

  if (!habits || habits.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Icon iconName="GreyCalendarIcon" width="3.5rem" height="3.5rem" />
        <h3 className={styles.emptyTitle}>No tracking history</h3>
        <p className={styles.emptyText}>
          You haven't tracked any habits yet. Once you start tracking, your history will appear here.
        </p>
      </div>
    );
  }

  const grouped = groupByStatus(habits);

  return (
    <div className={styles.container}>
      {SECTIONS.map(({ key, label }) => {
        const items = grouped[key];
        if (items.length === 0) return null;
        return (
          <div key={key} className={styles.section}>
            <h3 className={styles.sectionTitle}>{label}</h3>
            {items.map((habit, i) => (
              <HabitCard key={habit.id} habit={habit} status={key} index={i} />
            ))}
          </div>
        );
      })}
    </div>
  );
};

export default HabitTrackingHistory;
